import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEdit,
  faTrash,
  faSave,
  faTimes,
  faExclamationTriangle,
  faDatabase,
  faBuilding,
  faShield,
  faCog,
  faUpload,
  faFileImport,
  faSpinner,
  faMagic,
  faChartBar,
  faDownload,
  faCloudUpload,
  faCalendarAlt,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';

export interface CustomCostEntry {
  id: string;
  vendorName: string;
  costType: 'SaaS' | 'License' | 'Security Tool' | 'Other';
  amount: number;
  currency: string;
  month: string;              // YYYY-MM format for monthly tracking
  dateRange: {
    start: string;
    end: string;
  };
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface CSVImportResult {
  entries: CustomCostEntry[];
  summary: {
    totalEntries: number;
    totalCost: number;
    categories: Record<string, number>;
    insights: string[];
    visualizations: {
      chartType: string;
      data: any[];
      title: string;
    }[];
  };
}

interface CustomDataTabProps {
  entries?: CustomCostEntry[];
  onAddEntry?: (entry: Omit<CustomCostEntry, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdateEntry?: (id: string, entry: Partial<CustomCostEntry>) => void;
  onDeleteEntry?: (id: string) => void;
  onCSVImport?: (result: CSVImportResult) => void;
  loading?: boolean;
}

interface EntryFormProps {
  entry?: CustomCostEntry;
  onSave: (entry: Omit<CustomCostEntry, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
}

interface CSVImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (result: CSVImportResult) => void;
}

// CSV Import Modal Component
const CSVImportModal: React.FC<CSVImportModalProps> = ({ isOpen, onClose, onImport }) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [previewData, setPreviewData] = useState<any[] | null>(null);
  const [mappings, setMappings] = useState<Record<string, string>>({
    vendorName: '',
    costType: '',
    amount: '',
    currency: '',
    startDate: '',
    endDate: '',
    notes: ''
  });
  const [analysis, setAnalysis] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (selectedFile: File) => {
    if (!selectedFile.name.endsWith('.csv')) {
      alert('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    
    // Parse CSV for preview
    const text = await selectedFile.text();
    const lines = text.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const rows = lines.slice(1, 6).map(line => 
      line.split(',').map(cell => cell.trim().replace(/"/g, ''))
    );
    
    const preview = rows.map(row => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index] || '';
      });
      return obj;
    });

    setPreviewData(preview);
    
    // Auto-detect common column mappings
    const detectedMappings: Record<string, string> = {};
    headers.forEach(header => {
      const lower = header.toLowerCase();
      if (lower.includes('vendor') || lower.includes('name') || lower.includes('company')) {
        detectedMappings.vendorName = header;
      }
      if (lower.includes('cost') || lower.includes('amount') || lower.includes('price')) {
        detectedMappings.amount = header;
      }
      if (lower.includes('type') || lower.includes('category')) {
        detectedMappings.costType = header;
      }
      if (lower.includes('currency')) {
        detectedMappings.currency = header;
      }
      if (lower.includes('start') || lower.includes('from')) {
        detectedMappings.startDate = header;
      }
      if (lower.includes('end') || lower.includes('to')) {
        detectedMappings.endDate = header;
      }
      if (lower.includes('note') || lower.includes('description')) {
        detectedMappings.notes = header;
      }
    });
    
    setMappings(detectedMappings);
  };

  const generateLLMAnalysis = async (csvData: any[]) => {
    // Simulate LLM analysis with intelligent insights
    const totalCost = csvData.reduce((sum, row) => {
      const amount = parseFloat(row[mappings.amount] || '0');
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);

    const categories: Record<string, number> = {};
    const vendors = new Set();
    
    csvData.forEach(row => {
      const category = row[mappings.costType] || 'Other';
      const amount = parseFloat(row[mappings.amount] || '0');
      categories[category] = (categories[category] || 0) + amount;
      vendors.add(row[mappings.vendorName] || 'Unknown');
    });

    // Generate intelligent insights
    const insights = [
      `Analyzed ${csvData.length} cost entries with total spend of $${totalCost.toLocaleString()}`,
      `Found ${vendors.size} unique vendors across ${Object.keys(categories).length} categories`,
      `Top spending category: ${Object.entries(categories).sort(([,a], [,b]) => b - a)[0]?.[0] || 'N/A'}`,
      `Average cost per entry: $${(totalCost / csvData.length).toFixed(2)}`,
      totalCost > 50000 ? 'High-value dataset detected - consider cost optimization strategies' : 'Moderate spending levels detected',
      Object.keys(categories).length > 5 ? 'Diverse spending across multiple categories - good for comprehensive analysis' : 'Focused spending pattern detected'
    ];

    // Generate visualizations
    const visualizations = [
      {
        chartType: 'pie',
        title: 'Cost Distribution by Category',
        data: Object.entries(categories).map(([name, value]) => ({ name, value }))
      },
      {
        chartType: 'bar',
        title: 'Top Vendors by Cost',
        data: Array.from(vendors).slice(0, 10).map(vendor => ({
          name: vendor,
          value: csvData.filter(row => row[mappings.vendorName] === vendor)
            .reduce((sum, row) => sum + parseFloat(row[mappings.amount] || '0'), 0)
        })).sort((a, b) => b.value - a.value)
      }
    ];

    return {
      totalCost,
      categories,
      insights,
      visualizations
    };
  };

  const handleImport = async () => {
    if (!file || !previewData) return;

    setImporting(true);
    
    try {
      // Parse full CSV
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const rows = lines.slice(1).map(line => 
        line.split(',').map(cell => cell.trim().replace(/"/g, ''))
      );
      
      const fullData = rows.map(row => {
        const obj: any = {};
        headers.forEach((header, index) => {
          obj[header] = row[index] || '';
        });
        return obj;
      });

      // Generate LLM analysis
      const analysis = await generateLLMAnalysis(fullData);

      // Convert to CustomCostEntry format
      const entries: CustomCostEntry[] = fullData.map((row, index) => {
        const amount = parseFloat(row[mappings.amount] || '0');
        const startDate = row[mappings.startDate] || new Date().toISOString().split('T')[0];

        // Auto-calculate month from start date (YYYY-MM format)
        const month = startDate.substring(0, 7); // Extract YYYY-MM

        return {
          id: `csv-import-${Date.now()}-${index}`,
          vendorName: row[mappings.vendorName] || 'Unknown Vendor',
          costType: (row[mappings.costType] || 'Other') as any,
          amount: isNaN(amount) ? 0 : amount,
          currency: row[mappings.currency] || 'USD',
          month: month,
          dateRange: {
            start: startDate,
            end: row[mappings.endDate] || new Date().toISOString().split('T')[0]
          },
          notes: row[mappings.notes] || `Imported from CSV: ${file.name}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      });

      const result: CSVImportResult = {
        entries,
        summary: {
          totalEntries: entries.length,
          totalCost: analysis.totalCost,
          categories: analysis.categories,
          insights: analysis.insights,
          visualizations: analysis.visualizations
        }
      };

      onImport(result);
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      alert('Failed to import CSV. Please check the file format.');
    } finally {
      setImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-300">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xs font-semibold text-gray-900">Import Cost Data from CSV</h2>
              <p className="text-xs text-gray-600 mt-1">
                Upload a CSV file and let our AI analyze and categorize your cost data
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FontAwesomeIcon icon={faTimes} className="text-lg" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-3">
          {/* File Upload */}
          {!file && (
            <div
              className={`border-2 border-dashed rounded p-8 text-center transition-colors ${
                dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <FontAwesomeIcon icon={faCloudUpload} className="text-4xl text-gray-400 mb-4" />
              <div className="text-xs font-medium text-gray-900 mb-2">
                Drop your CSV file here or click to browse
              </div>
              <div className="text-xs text-gray-600 mb-4">
                Supports CSV files with cost data from vendors, SaaS tools, or billing systems
              </div>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Choose File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileInput}
                className="hidden"
              />
            </div>
          )}

          {/* File Info and Preview */}
          {file && previewData && (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon icon={faFileImport} className="text-green-600" />
                  <div>
                    <div className="font-medium text-green-900">{file.name}</div>
                    <div className="text-xs text-green-700">Ready for import</div>
                  </div>
                </div>
              </div>

              {/* Column Mapping */}
              <div>
                <h3 className="text-xs font-medium text-gray-900 mb-4">Map CSV Columns</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(mappings).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        {key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}
                      </label>
                      <select
                        value={value}
                        onChange={(e) => setMappings({...mappings, [key]: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs"
                      >
                        <option value="">Select column...</option>
                        {previewData.length > 0 && Object.keys(previewData[0]).map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Preview */}
              <div>
                <h3 className="text-xs font-medium text-gray-900 mb-4">Data Preview</h3>
                <div className="overflow-x-auto border border-gray-300 rounded">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {previewData.length > 0 && Object.keys(previewData[0]).map(header => (
                          <th key={header} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.slice(0, 5).map((row, index) => (
                        <tr key={index}>
                          {Object.values(row).map((cell: any, cellIndex) => (
                            <td key={cellIndex} className="px-4 py-2 text-xs text-gray-900">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  Showing first 5 rows of {previewData.length} total rows
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-300 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
          >
            Cancel
          </button>
          {file && (
            <>
              <button
                onClick={() => {
                  setFile(null);
                  setPreviewData(null);
                  setMappings({
                    vendorName: '',
                    costType: '',
                    amount: '',
                    currency: '',
                    startDate: '',
                    endDate: '',
                    notes: ''
                  });
                }}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Choose Different File
              </button>
              <button
                onClick={handleImport}
                disabled={importing || !mappings.vendorName || !mappings.amount}
                className="px-4 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importing ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Analyzing & Importing...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagic} className="mr-2" />
                    Import & Analyze
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const EntryForm: React.FC<EntryFormProps> = ({ entry, onSave, onCancel }) => {
  const today = new Date();
  const firstOfMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const lastOfMonthStr = `${lastOfMonth.getFullYear()}-${String(lastOfMonth.getMonth() + 1).padStart(2, '0')}-${String(lastOfMonth.getDate()).padStart(2, '0')}`;
  const currentMonth = firstOfMonth.substring(0, 7);

  const [formData, setFormData] = useState({
    vendorName: entry?.vendorName || '',
    costType: entry?.costType || 'SaaS' as const,
    amount: entry?.amount || 0,
    currency: entry?.currency || 'USD',
    month: entry?.month || currentMonth,
    dateRange: {
      start: entry?.dateRange?.start || firstOfMonth,
      end: entry?.dateRange?.end || lastOfMonthStr
    },
    notes: entry?.notes || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.vendorName.trim()) {
      newErrors.vendorName = 'Vendor name is required';
    }
    
    if (formData.amount <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    
    if (!formData.dateRange.start) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (!formData.dateRange.end) {
      newErrors.endDate = 'End date is required';
    }
    
    if (formData.dateRange.start && formData.dateRange.end && 
        new Date(formData.dateRange.start) >= new Date(formData.dateRange.end)) {
      newErrors.dateRange = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="bg-white border border-gray-300 rounded p-3">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold text-gray-900">
          {entry ? 'Edit Custom Cost Entry' : 'Add New Custom Cost Entry'}
        </h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Vendor Name */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Vendor Name *
            </label>
            <input
              type="text"
              value={formData.vendorName}
              onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
              className={`w-full px-3 py-2 border rounded-md text-xs ${
                errors.vendorName ? 'border-red-300' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="e.g., Slack, Jira, DataDog"
            />
            {errors.vendorName && (
              <p className="text-red-600 text-xs mt-1">{errors.vendorName}</p>
            )}
          </div>

          {/* Cost Type */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Cost Type *
            </label>
            <select
              value={formData.costType}
              onChange={(e) => setFormData({ ...formData, costType: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="SaaS">SaaS</option>
              <option value="License">License</option>
              <option value="Security Tool">Security Tool</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Amount *
            </label>
            <div className="relative">
              <span className="absolute left-3 top-2 text-gray-500 text-xs">$</span>
              <input
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) || 0 })}
                className={`w-full pl-8 pr-3 py-2 border rounded-md text-xs ${
                  errors.amount ? 'border-red-300' : 'border-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>
            {errors.amount && (
              <p className="text-red-600 text-xs mt-1">{errors.amount}</p>
            )}
          </div>

          {/* Currency */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Currency
            </label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
              <option value="JPY">JPY</option>
            </select>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <input
              type="date"
              value={formData.dateRange.start}
              onChange={(e) => {
                const startDate = e.target.value;
                // Auto-calculate month from start date
                const month = startDate ? startDate.substring(0, 7) : '';
                setFormData({
                  ...formData,
                  month: month,
                  dateRange: { ...formData.dateRange, start: startDate }
                });
              }}
              className={`w-full px-3 py-2 border rounded-md text-xs ${
                errors.startDate ? 'border-red-300' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.startDate && (
              <p className="text-red-600 text-xs mt-1">{errors.startDate}</p>
            )}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              End Date *
            </label>
            <input
              type="date"
              value={formData.dateRange.end}
              onChange={(e) => setFormData({ 
                ...formData, 
                dateRange: { ...formData.dateRange, end: e.target.value }
              })}
              className={`w-full px-3 py-2 border rounded-md text-xs ${
                errors.endDate ? 'border-red-300' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.endDate && (
              <p className="text-red-600 text-xs mt-1">{errors.endDate}</p>
            )}
          </div>
        </div>

        {errors.dateRange && (
          <p className="text-red-600 text-xs">{errors.dateRange}</p>
        )}

        {/* Notes */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Additional details about this cost entry..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-xs font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
          >
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            {entry ? 'Update Entry' : 'Add Entry'}
          </button>
        </div>
      </form>
    </div>
  );
};

export const CustomDataTab: React.FC<CustomDataTabProps> = ({
  entries = [],
  onAddEntry,
  onUpdateEntry,
  onDeleteEntry,
  onCSVImport,
  loading = false
}) => {
  // Get current month in YYYY-MM format
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CustomCostEntry | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState<CSVImportResult | null>(null);

  const handleAddEntry = (entryData: Omit<CustomCostEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    onAddEntry?.(entryData);
    setShowForm(false);
  };

  const handleEditEntry = (entry: CustomCostEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleUpdateEntry = (entryData: Omit<CustomCostEntry, 'id' | 'createdAt' | 'updatedAt'>) => {
    if (editingEntry) {
      onUpdateEntry?.(editingEntry.id, entryData);
      setEditingEntry(null);
      setShowForm(false);
    }
  };

  const handleDeleteEntry = (id: string) => {
    onDeleteEntry?.(id);
    setDeleteConfirm(null);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  const handleCSVImport = (result: CSVImportResult) => {
    setImportResult(result);
    onCSVImport?.(result);
  };

  // Month navigation helpers
  const navigateMonth = (direction: 'prev' | 'next') => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);

    if (direction === 'prev') {
      date.setMonth(date.getMonth() - 1);
    } else {
      date.setMonth(date.getMonth() + 1);
    }

    const newMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    setSelectedMonth(newMonth);
  };

  const formatMonthDisplay = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Filter entries by selected month
  const filteredEntries = entries.filter(entry => entry.month === selectedMonth);

  // Get unique months from all entries for navigation
  const availableMonths = Array.from(new Set(entries.map(e => e.month))).sort().reverse();

  const getCostTypeIcon = (type: string) => {
    switch (type) {
      case 'SaaS': return faDatabase;
      case 'License': return faBuilding;
      case 'Security Tool': return faShield;
      default: return faCog;
    }
  };

  const getCostTypeColor = (type: string) => {
    switch (type) {
      case 'SaaS': return 'bg-blue-100 text-blue-600';
      case 'License': return 'bg-green-100 text-green-600';
      case 'Security Tool': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Calculate totals for the selected month
  const monthlyTotal = filteredEntries.reduce((sum, entry) => sum + entry.amount, 0);
  const totalCustomCosts = entries.reduce((sum, entry) => sum + entry.amount, 0);

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="bg-white border border-gray-300 rounded p-3 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-50 to-teal-50 border border-green-200 rounded p-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xs font-semibold text-gray-900 mb-1">Custom Cost Data</h2>
            <p className="text-xs text-gray-600">
              Manage external costs from third-party vendors, SaaS tools, and CSV imports
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold text-gray-900">${totalCustomCosts.toLocaleString()}</div>
            <div className="text-xs text-gray-600">{entries.length} total entries</div>
          </div>
        </div>
      </div>

      {/* Month Selector */}
      {entries.length > 0 && (
        <div className="bg-white border border-gray-300 rounded p-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigateMonth('prev')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title="Previous month"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>

            <div className="flex items-center space-x-3">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600" />
              <div className="text-center">
                <div className="text-xs font-semibold text-gray-900">
                  {formatMonthDisplay(selectedMonth)}
                </div>
                <div className="text-xs text-gray-600">
                  ${monthlyTotal.toLocaleString()} • {filteredEntries.length} entries
                </div>
              </div>
            </div>

            <button
              onClick={() => navigateMonth('next')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
              title="Next month"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          {/* Month Quick Navigation */}
          {availableMonths.length > 1 && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="text-xs text-gray-600 mb-2">Quick Jump:</div>
              <div className="flex flex-wrap gap-2">
                {availableMonths.map(month => (
                  <button
                    key={month}
                    onClick={() => setSelectedMonth(month)}
                    className={`px-3 py-1 text-xs rounded transition-colors ${
                      month === selectedMonth
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {formatMonthDisplay(month)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import Analysis Results */}
      {importResult && (
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded p-3">
          <div className="flex items-center space-x-3 mb-4">
            <FontAwesomeIcon icon={faMagic} className="text-purple-600 text-lg" />
            <h3 className="text-xs font-semibold text-gray-900">AI Analysis Results</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded p-3">
              <div className="text-xs text-gray-600">Total Imported</div>
              <div className="text-xs font-bold text-gray-900">{importResult.summary.totalEntries} entries</div>
            </div>
            <div className="bg-white rounded p-3">
              <div className="text-xs text-gray-600">Total Cost</div>
              <div className="text-xs font-bold text-gray-900">${importResult.summary.totalCost.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded p-3">
              <div className="text-xs text-gray-600">Categories</div>
              <div className="text-xs font-bold text-gray-900">{Object.keys(importResult.summary.categories).length}</div>
            </div>
          </div>

          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">AI Insights:</h4>
            {importResult.summary.insights.map((insight, index) => (
              <div key={index} className="text-xs text-gray-700 flex items-start space-x-2">
                <span className="text-purple-500">•</span>
                <span>{insight}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!showForm && (
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-4 py-2 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition-colors"
          >
            <FontAwesomeIcon icon={faUpload} className="mr-2" />
            Import CSV
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Add Manual Entry
          </button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <EntryForm
          entry={editingEntry || undefined}
          onSave={editingEntry ? handleUpdateEntry : handleAddEntry}
          onCancel={handleCancelForm}
        />
      )}

      {/* Entries List */}
      {entries.length > 0 ? (
        filteredEntries.length > 0 ? (
          <div className="bg-white border border-gray-300 rounded overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-300">
              <h3 className="text-xs font-semibold text-gray-900">
                Cost Entries for {formatMonthDisplay(selectedMonth)}
              </h3>
            </div>
            <div className="divide-y divide-gray-200">
              {filteredEntries.map((entry) => (
                <div key={entry.id} className="p-6 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className={`w-10 h-10 rounded flex items-center justify-center ${getCostTypeColor(entry.costType)}`}>
                        <FontAwesomeIcon icon={getCostTypeIcon(entry.costType)} />
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-gray-900">{entry.vendorName}</div>
                        <div className="text-xs text-gray-600">{entry.costType}</div>
                        <div className="text-xs text-gray-500">
                          {entry.dateRange?.start && entry.dateRange?.end
                            ? `${new Date(entry.dateRange.start).toLocaleDateString()} - ${new Date(entry.dateRange.end).toLocaleDateString()}`
                            : (entry.month ? formatMonthDisplay(entry.month) : '—')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-xs font-bold text-gray-900">
                          ${entry.amount.toLocaleString()} {entry.currency}
                        </div>
                        {entry.notes && (
                          <div className="text-xs text-gray-600 max-w-xs truncate">
                            {entry.notes}
                          </div>
                        )}
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditEntry(entry)}
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit entry"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(entry.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete entry"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-300 rounded p-12 text-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-6xl text-gray-300 mb-4" />
            <h3 className="text-xs font-semibold text-gray-900 mb-2">No Entries for {formatMonthDisplay(selectedMonth)}</h3>
            <p className="text-xs text-gray-600 mb-6 max-w-md mx-auto">
              There are no cost entries for this month. Use the month navigation to view other months,
              or add a new entry for this month.
            </p>
            <button
              onClick={() => setShowForm(true)}
              className="px-6 py-3 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add Entry for {formatMonthDisplay(selectedMonth)}
            </button>
          </div>
        )
      ) : (
        !showForm && (
          <div className="bg-white border border-gray-300 rounded p-12 text-center">
            <FontAwesomeIcon icon={faDatabase} className="text-6xl text-gray-300 mb-4" />
            <h3 className="text-xs font-semibold text-gray-900 mb-2">No Custom Data Entries</h3>
            <p className="text-xs text-gray-600 mb-6 max-w-md mx-auto">
              Import cost data from CSV files or add manual entries to include external vendor costs, 
              SaaS subscriptions, or other third-party expenses in your analysis.
            </p>
            <div className="flex justify-center space-x-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="px-6 py-3 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 transition-colors"
              >
                <FontAwesomeIcon icon={faUpload} className="mr-2" />
                Import from CSV
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="px-6 py-3 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="mr-2" />
                Add Manual Entry
              </button>
            </div>
          </div>
        )
      )}

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImport={handleCSVImport}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded p-3 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-lg" />
              <h3 className="text-xs font-semibold text-gray-900">Confirm Deletion</h3>
            </div>
            <p className="text-xs text-gray-600 mb-6">
              Are you sure you want to delete this cost entry? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEntry(deleteConfirm)}
                className="px-4 py-2 text-xs font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 transition-colors"
              >
                Delete Entry
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};